import { CalendarPlusIcon, TrashIcon } from '@icons/index.js';
import { UIButton, UIDiv, UIInput, UIText } from '@ui';
import { formatDateForInput } from '@utils/dateUtils';
import flatpickr from 'flatpickr';

// --- Flatpickr Singleton Manager ---
class FlatpickrSingleton {
  constructor() {
    this.instance = null;
    this.currentInput = null;
    this.currentOptions = null;
  }

  attach(input, options = {}) {
    if (this.instance) {
      this.instance.destroy();
      this.instance = null;
    }
    this.currentInput = input;
    this.currentOptions = options;
    this.instance = flatpickr(input, options);
  }

  detach(input) {
    if (this.instance && this.currentInput === input) {
      this.instance.destroy();
      this.instance = null;
      this.currentInput = null;
      this.currentOptions = null;
    }
  }

  setTheme(isDark) {
    if (this.instance) {
      this.instance.set('theme', isDark ? 'dark' : 'light');
    }
  }
}

const flatpickrSingleton = new FlatpickrSingleton();

const types = {
  id: '',
  onUpdate: val => {},
  onClose: () => {},
  showTitle: true,
  title: null,
  placeholder: 'Due Date',
  initialValue: null,
  showIcon: true,
  readonly: false,
};

export default class DueDate extends UIDiv {
  constructor({
    id,
    onUpdate,
    onClose,
    showTitle = true,
    title,
    placeholder,
    initialValue = null,
    showIcon = true,
    defaultDate = null,
    readonly = false,
  } = types) {
    super();
    this.updateCallback = onUpdate;
    this.closeCallback = onClose;
    this.setId(id);
    this.setClass('duedate-container');
    this.showTitle = showTitle;
    this.title = title || i18next.t('sidebar.comments.task_duedate');
    this.placeholder = placeholder;
    this.initialValue = initialValue;
    this.showIcon = showIcon;
    this.defaultDate = defaultDate;
    this.readonly = readonly;
    this.#build();
  }

  #build() {
    if (this.showTitle) {
      const dueDateTitle = new UIText(this.title);
      dueDateTitle.setClass('font-medium text-sm');
      this.add(dueDateTitle);
    }

    const inputWrapper = new UIDiv();
    inputWrapper.setClass(
      'relative w-full flex items-center justify-center gap-2',
    );

    this.input = new UIInput('');
    this.input.setClass('date-input px-3 rounded-md min-h-[34px]');
    if (this.readonly) {
      this.input.addClass('readonly');
      this.input.dom.readOnly = true;
    }
    this.input.setValue(this.initialValue || '');
    this.input.setPlaceholder(this.placeholder);
    this.input.setType('text');
    this.input.setId(
      this.id
        ? `input-${this.id}`
        : `input-${Math.random().toString(36).substring(2, 15)}`,
    );
    inputWrapper.add(this.input);

    // Add Escape key handler to close flatpickr
    this.input.onKeyDown(e => {
      e.stopPropagation();
      e.preventDefault();
      if (
        e.key === 'Escape' &&
        flatpickrSingleton.instance &&
        flatpickrSingleton.currentInput === this.input.dom &&
        flatpickrSingleton.instance.isOpen
      ) {
        flatpickrSingleton.instance.close();
      }
    });

    if (
      !this.readonly &&
      this.showIcon &&
      window.innerWidth > 450 &&
      window.innerHeight > 650
    ) {
      const iconWrapper = new UIDiv();

      // Create calendar icon
      this.calendarIcon = new CalendarPlusIcon({
        color: 'none',
      });
      this.calendarIcon.setClass('calendar-icon');

      // Create clear button with trash icon
      this.clearButtonWrapper = new UIButton('');
      this.clearButtonWrapper.setClass(
        'calendar-icon flex items-center justify-center bg-transparent border-0 p-0 cursor-pointer',
      );
      this.clearButtonWrapper.setStyle('display', ['none']);

      this.clearButton = new TrashIcon({
        color: 'none',
        strokeColor: 'currentColor',
      });
      this.clearButton.setClass('w-full h-full');

      this.clearButtonWrapper.add(this.clearButton);

      this.clearButtonWrapper.dom.addEventListener('click', e => {
        e.stopPropagation();
        if (this.updateCallback) {
          this.updateCallback(null);
        }
        this.setValue(this.defaultDate);
        this.toggleIcons(false);
      });

      iconWrapper.add(this.calendarIcon);
      iconWrapper.add(this.clearButtonWrapper);
      inputWrapper.add(iconWrapper);

      // Set initial icon state
      this.toggleIcons(!!this.initialValue);
    }

    this.add(inputWrapper);

    // Only initialize flatpickr if not readonly
    if (!this.readonly) {
      // Attach singleton flatpickr on focus or calendar icon click
      const openPicker = () => {
        flatpickrSingleton.attach(this.input.dom, {
          disableMobile: true,
          dateFormat: 'Y-m-d',
          altInput: true,
          altFormat: 'M j Y',
          minDate: 'today',
          defaultDate: this.input.getValue() || this.defaultDate || null,
          theme: 'light',
          onChange: selectedDates => {
            const value = selectedDates[0]
              ? formatDateForInput(selectedDates[0])
              : null;
            if (this.updateCallback) {
              this.updateCallback(value);
            }
            this.setValue(value);
            this.toggleIcons(!!value);
          },
          onOpen: () => {
            if (flatpickrSingleton.instance) {
              flatpickrSingleton.instance.calendarContainer.style.zIndex =
                '9999';
              flatpickrSingleton.instance.altInput.focus();
            }
          },
          onClose: () => {
            if (this.closeCallback) {
              this.closeCallback();
            }
            requestAnimationFrame(() =>
              flatpickrSingleton.detach(this.input.dom),
            );
          },
        });

        // If altInput is used, also style the alternative input
        requestAnimationFrame(() => {
          if (
            flatpickrSingleton.instance &&
            flatpickrSingleton.instance.altInput
          ) {
            flatpickrSingleton.instance.altInput.classList.add(
              'ring-2',
              'ring-[var(--o-hive-primary-80)]',
              'focus-visible:border-[var(--o-hive-primary-80)]',
              'min-h-[34px]',
              'cursor-text',
              'focus-visible:outline-none',
              'px-3',
              'rounded-md',
            );
          }
          flatpickrSingleton.instance.open();
        }, 0);
      };

      this.input.dom.addEventListener('focus', openPicker);
      if (this.calendarIcon) {
        this.calendarIcon.dom.addEventListener('click', e => {
          e.stopPropagation();
          this.input.dom.focus();
          openPicker();
        });
      }
    }
  }

  toggleIcons(hasValue) {
    if (this.calendarIcon && this.clearButtonWrapper) {
      this.calendarIcon.setStyle('display', [hasValue ? 'none' : 'block']);
      this.clearButtonWrapper.setStyle('display', [
        hasValue ? 'block' : 'none',
      ]);
    }
  }

  // Removed per-instance flatpickr initialization

  async updateTheme(isDark) {
    // Only update theme if this input is currently active
    if (flatpickrSingleton.currentInput === this.input.dom) {
      flatpickrSingleton.setTheme(isDark);
      const currentValue = this.input.getValue();
      if (currentValue) {
        this.input.setValue(currentValue);
      }
    }
  }

  getValue() {
    const value = this.input ? this.input.getValue() : '';
    return value || null;
  }

  setValue(value) {
    if (this.input) {
      this.input.setValue(value || '');
      if (
        flatpickrSingleton.currentInput === this.input.dom &&
        flatpickrSingleton.instance
      ) {
        flatpickrSingleton.instance.setDate(value || null);
      }
      this.toggleIcons(!!value);
    }
  }

  destroy() {
    if (flatpickrSingleton.currentInput === this.input?.dom) {
      flatpickrSingleton.detach(this.input.dom);
    }
    super.destroy();
  }
}
